import React, { useState } from 'react';
import { WEBApi } from '../API/WEBApi';
import { v4 as uuidv4 } from 'uuid';

const CHUNK_SIZE = 500 * 1024; // 500 KB

const CreateImageUpload: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
    setUploadedUrl('');
    setMessage(null);
    setError(null);
    setUploadProgress(0);
  };

  const uploadInChunks = async (file: File) => {
    const fileId = uuidv4();
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    for (let i = 0; i < totalChunks; i++) {
      const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      const formData = new FormData();
      formData.append('chunk', chunk);
      formData.append('chunkIndex', i.toString());
      formData.append('fileId', fileId);

      await WEBApi.uploadImageChunk(formData);

      const progress = Math.round(((i + 1) / totalChunks) * 100);
      setUploadProgress(progress);
    }

    const res = await WEBApi.mergeChunks({
      fileId,
      totalChunks,
      originalName: file.name,
    });

    return res;
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Please select an image to upload.');
      return;
    }

    setUploading(true);
    setMessage(null);
    setError(null);
    setUploadProgress(0);

    try {
      const res = await uploadInChunks(file);
      setUploadedUrl(res.url);
      setMessage('Image uploaded successfully!');
    } catch (err) {
      console.error(err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="container mt-5">
      <h3>Upload Image</h3>
      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleUpload}>
        <div className="mb-3">
          <label htmlFor="imageUpload" className="form-label">Select Image</label>
          <input
            type="file"
            id="imageUpload"
            className="form-control"
            accept="image/*"
            onChange={handleFileChange}
            disabled={uploading}
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={uploading}>
          {uploading ? 'Uploading...' : 'Upload Image'}
        </button>
      </form>

      {uploading && (
        <div className="progress my-3">
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            style={{ width: `${uploadProgress}%` }}
            aria-valuenow={uploadProgress}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            {uploadProgress}%
          </div>
        </div>
      )}

      {uploadedUrl && (
        <div className="mt-4">
          <h6>Uploaded Image Preview:</h6>
          <img
            src={uploadedUrl}
            alt="Uploaded"
            style={{ width: '300px', borderRadius: '8px', border: '1px solid #ccc' }}
          />
          <p className="small mt-2 text-muted">{uploadedUrl}</p>
        </div>
      )}
    </div>
  );
};

export default CreateImageUpload;
